import { useCallback, useEffect, useRef, useState } from 'react';
import { API_BASE } from '../../apiConfig';
import { METRIC_KEYS, parseMetric } from '../../seriesExtractors';

/**
 * Loads every metric of a session in parallel — GET /api/sessions/:id/:metric?format=json.
 * Per metric: { status, data, code }
 *   loading     request in flight
 *   ok          array with records
 *   empty       nothing recorded (404 = no file for that metric, 204, or an empty array)
 *   processing  202 — the session isn't consolidated yet (the request also triggers consolidation)
 *   failed      any other error (code = HTTP status, or 0 for a network failure)
 */
export default function useSessionMetrics(id, authFetch) {
    const [metrics, setMetrics] = useState({});
    const [settled, setSettled] = useState(false);
    const gen = useRef(0);

    const fetchOne = useCallback(async (key, g) => {
        const put = (v) => { if (g === gen.current) setMetrics((s) => ({ ...s, [key]: v })); };
        setMetrics((s) => ({ ...s, [key]: { ...(s[key] || {}), status: 'loading' } }));
        try {
            const res = await authFetch(`${API_BASE}/api/sessions/${id}/${key}?format=json`);
            if (res.status === 202) return put({ status: 'processing', data: null, code: 202 });
            if (res.status === 204 || res.status === 404) return put({ status: 'empty', data: [], code: res.status });
            if (!res.ok) return put({ status: 'failed', data: null, code: res.status });
            const arr = parseMetric(await res.text());
            if (arr == null) return put({ status: 'failed', data: null, code: res.status });
            return put(arr.length ? { status: 'ok', data: arr, code: res.status } : { status: 'empty', data: [], code: res.status });
        } catch (e) {
            return put({ status: 'failed', data: null, code: 0 });
        }
    }, [authFetch, id]);

    const loadAll = useCallback(async () => {
        const g = ++gen.current;
        setSettled(false);
        setMetrics({});
        await Promise.all(METRIC_KEYS.map((k) => fetchOne(k, g)));
        if (g === gen.current) setSettled(true);
    }, [fetchOne]);

    const retry = useCallback((keys) => Promise.all(keys.map((k) => fetchOne(k, gen.current))), [fetchOne]);

    useEffect(() => {
        if (id) loadAll();
        return () => { gen.current += 1; };
    }, [id, loadAll]);

    return { metrics, settled, loadAll, retry };
}
